import React, { useEffect, useState } from 'react';
import { useStore } from '@/state/store';
import { useI18n } from '@/i18n/I18nContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { exportDrawio } from '@/lib/drawio';
import { downloadText, exportBrowserPDF, exportPrintPDF } from '@/lib/pdf';
import { buildAllPageSVGs } from '@/lib/svgExport';
import { loadProjectDoc } from '@/lib/projectStore';
import { FolderOpen, FileCode2, FileText, Check, Clock, ArrowRightLeft, Trash2 } from 'lucide-react';

const safeName = (name) => (name || 'netzwerk').replace(/[^a-z0-9_\-]+/gi, '_');

export const ProjectSwitcherDialog = ({ open, onOpenChange }) => {
  const { t, language } = useI18n();
  const projectId = useStore((s) => s.projectId);
  const listProjects = useStore((s) => s.listProjects);
  const switchProject = useStore((s) => s.switchProject);
  const deleteProject = useStore((s) => s.deleteProject);
  const showPortVlanBadges = useStore((s) => s.showPortVlanBadges);
  const showVlanLegend = useStore((s) => s.showVlanLegend);
  const showPortLabels = useStore((s) => s.showPortLabels);

  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(null); // `${id}:${kind}` while exporting
  const [pendingDelete, setPendingDelete] = useState(null); // project pending confirmation

  useEffect(() => {
    if (open) setProjects(listProjects());
  }, [open, listProjects]);

  const fmtDate = (ts) => {
    if (!ts) return '—';
    try {
      return new Date(ts).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return new Date(ts).toLocaleString();
    }
  };

  const handleSwitch = (id) => {
    switchProject(id);
    onOpenChange(false);
    toast.success(t('project_switched'));
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    deleteProject(pendingDelete.id);
    setPendingDelete(null);
    setProjects(listProjects());
    toast.success(t('project_deleted'));
  };

  const handleDownloadDrawio = (p) => {
    const doc = loadProjectDoc(p.id);
    if (!doc) return;
    try {
      const xml = exportDrawio(doc, language);
      downloadText(xml, `${safeName(doc.name)}.drawio`, 'application/xml');
      toast.success(t('toast_export_ok'));
    } catch {
      toast.error(t('toast_import_err'));
    }
  };

  const handleDownloadPdf = async (p) => {
    const doc = loadProjectDoc(p.id);
    if (!doc) return;
    setBusy(`${p.id}:pdf`);
    try {
      const svgs = buildAllPageSVGs(doc, language, { showBadges: showPortVlanBadges, showLegend: showVlanLegend, showPortLabels }).map((s) => s.svg);
      try {
        await exportBrowserPDF(svgs, `${safeName(doc.name)}.pdf`);
        toast.success(t('toast_pdf_ok'));
      } catch {
        const ok = exportPrintPDF(svgs);
        if (ok) toast.success(t('toast_pdf_ok'));
        else toast.error(t('toast_pdf_err'));
      }
    } catch {
      toast.error(t('toast_pdf_err'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="project-switcher-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5" /> {t('switch_project')}
          </DialogTitle>
          <DialogDescription>{t('switch_project_desc')}</DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-2 overflow-auto pr-1" data-testid="project-list">
          {projects.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">{t('no_projects')}</p>
          ) : (
            projects.map((p) => {
              const isCurrent = p.id === projectId;
              return (
                <div
                  key={p.id}
                  className={`flex items-center gap-3 rounded-md border p-3 ${isCurrent ? 'border-[#0B5FFF]/40 bg-[#0B5FFF]/5' : 'border-border bg-white'}`}
                  data-testid={`project-row-${p.id}`}
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-muted text-[#2A3138]">
                    <FolderOpen className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-[#111418]" data-testid={`project-name-${p.id}`}>
                        {p.name || t('untitled_project')}
                      </span>
                      {isCurrent && (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded bg-[#0B5FFF]/10 px-1.5 py-0.5 text-[10px] font-medium text-[#0B5FFF]" data-testid={`project-current-${p.id}`}>
                          <Check className="h-3 w-3" /> {t('project_current')}
                        </span>
                      )}
                    </div>
                    <span className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground" data-testid={`project-date-${p.id}`}>
                      <Clock className="h-3 w-3" /> {t('last_modified')}: {fmtDate(p.updatedAt)}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2"
                      onClick={() => handleDownloadDrawio(p)}
                      title={t('download_drawio')}
                      data-testid={`project-dl-drawio-${p.id}`}
                    >
                      <FileCode2 className="mr-1 h-3.5 w-3.5" /> .drawio
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2"
                      disabled={busy === `${p.id}:pdf`}
                      onClick={() => handleDownloadPdf(p)}
                      title={t('pdf_browser_hint')}
                      data-testid={`project-dl-pdf-${p.id}`}
                    >
                      <FileText className="mr-1 h-3.5 w-3.5" /> {t('export_pdf_browser')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-[#B42318]"
                      onClick={() => setPendingDelete(p)}
                      title={t('delete_project')}
                      aria-label={t('delete_project')}
                      data-testid={`project-delete-${p.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      className="h-8"
                      disabled={isCurrent}
                      onClick={() => handleSwitch(p.id)}
                      data-testid={`project-switch-${p.id}`}
                    >
                      <ArrowRightLeft className="mr-1 h-3.5 w-3.5" /> {t('switch')}
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <p className="flex items-center gap-1.5 border-t border-border pt-2.5 text-[11px] text-muted-foreground" data-testid="pdf-converter-note">
          <FileText className="h-3 w-3 shrink-0" /> {t('pdf_converter_note')}
        </p>

        <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
          <AlertDialogContent data-testid="project-delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('delete_project_title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('delete_project_desc', { name: pendingDelete?.name || t('untitled_project') })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="project-delete-cancel">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction
                className="bg-[#B42318] text-white hover:bg-[#992013]"
                onClick={confirmDelete}
                data-testid="project-delete-confirm-btn"
              >
                {t('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};
